/**
 * ComponentRenderer — Maps tool names from SSE `component` events
 * to the appropriate React visual component.
 *
 * This is the bridge between the backend's tool results and the
 * frontend's interactive components. Each tool that returns visual
 * data gets matched to a component here.
 */

import { Suspense, lazy } from "react";
import type { ComponentData } from "@/api/types";

// Lazy-load heavy components (maps, charts) for code splitting
const ComparableSalesMap = lazy(() =>
  import("@/components/map/ComparableSalesMap").then((m) => ({
    default: m.ComparableSalesMap,
  }))
);

const ScreeningReportCard = lazy(() => import("@/components/cards/ScreeningReportCard"));
const PriceTrendChart = lazy(() => import("@/components/charts/PriceTrendChart"));
const MarketSnapshotCard = lazy(() => import("@/components/cards/MarketSnapshotCard"));
const TaxImpactCard = lazy(() => import("@/components/cards/TaxImpactCard"));
const SiteSearchMap = lazy(() => import("@/components/map/SiteSearchMap"));
const SubmarketComparison = lazy(() => import("@/components/charts/SubmarketComparison"));
const ParcelDetailCard = lazy(() => import("@/components/cards/ParcelDetailCard"));
const COACard = lazy(() => import("@/components/cards/COACard"));
const DSRProgressCard = lazy(() => import("@/components/cards/DSRProgressCard"));
const SweepProgressCard = lazy(() => import("@/components/cards/SweepProgressCard"));
const DealIntelCard = lazy(() => import("@/components/cards/DealIntelCard"));
const ThreatOpportunityCard = lazy(() => import("@/components/cards/ThreatOpportunityCard"));

interface ComponentRendererProps {
  component: ComponentData;
  /** Callback to send follow-up messages to the agent */
  onFollowUp?: (message: string) => void;
}

export function ComponentRenderer({ component, onFollowUp }: ComponentRendererProps) {
  const { tool, data } = component;

  return (
    <Suspense fallback={<ComponentLoading tool={tool} />}>
      {renderComponent(tool, data, onFollowUp)}
    </Suspense>
  );
}

/**
 * Transform calculate_investor_tax tool output into TaxImpactCard props.
 * Tool returns nested current_owner / investor_projection structure;
 * the card expects flat current_tax / investor_tax / breakdown.
 */
function transformTaxData(raw: Record<string, unknown>) {
  const co = (raw.current_owner ?? {}) as Record<string, unknown>;
  const ip = (raw.investor_projection ?? {}) as Record<string, unknown>;
  const currentTax = (co.estimated_annual_tax ?? 0) as number;
  const investorTax = (ip.projected_annual_tax ?? 0) as number;
  const av = (raw.assessed_value ?? 0) as number;
  const rate = (raw.tax_rate_per_100 ?? 0) as number;
  const capStr = String(ip.cap_rate ?? "3%").replace("%", "");
  const capPct = parseFloat(capStr) || 3;
  const capAmount = av * (capPct / 100);

  // Build deductions_lost from the difference
  const deductionsLost: { name: string; amount: number }[] = [];
  const coDeductions = (co.deductions ?? 0) as number;
  if (coDeductions > 0) {
    deductionsLost.push({ name: "Homestead deductions", amount: coDeductions });
  }

  return {
    parcel_id: raw.parcel_id as string,
    address: raw.address as string | undefined,
    current_tax: currentTax,
    investor_tax: investorTax,
    tax_increase_pct: (ip.increase_percentage ?? 0) as number,
    investor_use: (ip.property_class ?? raw.investor_use ?? "commercial") as string,
    breakdown: {
      assessed_value: av,
      gross_rate: rate,
      cap_rate_pct: capPct,
      cap_amount: capAmount,
      deductions_lost: deductionsLost.length > 0 ? deductionsLost : undefined,
    },
  };
}

/**
 * Transform get_market_snapshot tool output into MarketSnapshotCard props.
 * Tool returns current_period/prior_period/yoy_change with _yoy_pct suffixes;
 * the card expects current/yoy_change with shorter key names.
 */
function transformMarketSnapshot(raw: Record<string, unknown>) {
  const cp = (raw.current_period ?? {}) as Record<string, unknown>;
  const yoy = (raw.yoy_change ?? {}) as Record<string, unknown>;

  return {
    geography: raw.geography as string,
    geography_type: raw.geography_type as string,
    property_type: raw.property_type as string | null,
    period_months: (raw.months_back ?? 12) as number,
    current: {
      sale_count: (cp.sale_count ?? 0) as number,
      median_price: (cp.median_price ?? 0) as number,
      median_price_per_sf: (cp.median_price_per_sf ?? null) as number | null,
      total_volume: (cp.total_volume ?? 0) as number,
      avg_sale_to_av: (cp.avg_sale_to_av_ratio ?? null) as number | null,
    },
    yoy_change: {
      price_pct: (yoy.median_price_yoy_pct ?? null) as number | null,
      volume_pct: (yoy.total_volume_yoy_pct ?? null) as number | null,
      count_pct: (yoy.sale_count_yoy_pct ?? null) as number | null,
    },
  };
}

/**
 * Transform get_sales_trend tool output into PriceTrendChart props.
 * Mostly pass-through — just strip extra fields the chart doesn't need.
 */
function transformSalesTrend(raw: Record<string, unknown>) {
  return {
    geography: raw.geography as string,
    property_type: (raw.property_type ?? null) as string | null,
    quarters: (raw.quarters ?? []) as Array<{
      quarter: string;
      sale_count: number;
      median_price: number;
      median_price_per_sf: number | null;
      total_volume: number;
    }>,
  };
}

/**
 * Transform find_similar_parcels tool output for SiteSearchMap.
 * Mostly pass-through — strip status field.
 */
function transformSiteSearch(raw: Record<string, unknown>) {
  return {
    search_center: raw.search_center as { latitude: number; longitude: number },
    search_params: raw.search_params as Record<string, unknown>,
    count: (raw.count ?? 0) as number,
    parcels: (raw.parcels ?? []) as Array<Record<string, unknown>>,
  };
}

/**
 * Transform compare_submarkets tool output for SubmarketComparison.
 * Tool returns submarkets as a dict keyed by geography name;
 * the chart expects an array of submarket entries.
 */
function transformSubmarkets(raw: Record<string, unknown>) {
  const submarketDict = (raw.submarkets ?? {}) as Record<string, Record<string, unknown>>;
  const submarkets = Object.values(submarketDict).map((sm) => ({
    geography: (sm.geography ?? "") as string,
    sale_count: (sm.sale_count ?? 0) as number,
    median_price: (sm.median_price ?? 0) as number,
    median_price_per_sf: (sm.median_price_per_sf ?? null) as number | null,
    total_volume: (sm.total_volume ?? 0) as number,
  }));

  return {
    geographies: submarkets.map((s) => s.geography),
    property_type: (raw.property_type ?? null) as string | null,
    months_back: (raw.months_back ?? 12) as number,
    submarkets,
  };
}

/**
 * Transform lookup_parcel_by_address / lookup_parcel_by_id for ParcelDetailCard.
 * by_address returns { parcels: [...] }, by_id returns { parcel: {...} }.
 * Extract the first parcel from either shape.
 */
function transformParcelDetail(raw: Record<string, unknown>) {
  // lookup_parcel_by_id: single parcel object
  if (raw.parcel && typeof raw.parcel === "object") {
    return raw.parcel as Record<string, unknown>;
  }
  // lookup_parcel_by_address: array of parcels — use first
  const parcels = raw.parcels as Array<Record<string, unknown>> | undefined;
  if (parcels && parcels.length > 0) {
    return parcels[0];
  }
  return raw;
}

/**
 * GAP_QUESTIONS shared by DealIntelCard and ThreatOpportunityCard transforms.
 */
const GAP_QUESTIONS_MAP: Record<string, string> = {
  "Property basic data incomplete": "Tell me more about this property",
  "Tax analysis not completed": "What would the taxes be if I bought this?",
  "Site screening not completed": "Screen this site for zoning, flood, and environmental issues",
  "Market analysis not completed": "What's the market like in this area?",
  "Owner research not completed": "Who owns this property and what else do they own?",
  "Financial structuring data missing": "Run a DSCR and LTV analysis",
};

/**
 * Transform DSR structure into DealIntelCard props.
 * Flattens ground_truth, estimates, and top-level DSR fields.
 */
function transformDealIntel(raw: Record<string, unknown>) {
  const gt = (raw.ground_truth ?? {}) as Record<string, unknown>;
  const est = (raw.estimates ?? {}) as Record<string, unknown>;
  const pir = (raw.pir_status ?? {}) as Record<string, string>;

  // Extract from ground_truth
  const address = (gt.address ?? raw.address ?? null) as string | null;
  const parcel_id = (gt.parcel_id ?? raw.parcel_id ?? null) as string | null;
  const property_type = (gt.property_type ?? null) as string | null;
  const assessed_value = (gt.assessed_value ?? null) as number | null;
  const building_sf = (gt.building_sf ?? null) as number | null;
  const acreage = (gt.acreage ?? null) as number | null;
  const current_owner = (gt.current_owner ?? null) as string | null;

  // Extract from estimates
  const risk_score = (est.risk_score ?? null) as number | null;
  const investor_tax_annual = (est.investor_tax_annual ?? null) as number | null;
  const tax_delta = (est.tax_delta ?? null) as number | null;
  const median_price = (est.median_price ?? null) as number | null;
  const median_price_per_sf = (est.median_price_per_sf ?? null) as number | null;

  const threats = (raw.threats ?? []) as string[];
  const opportunities = (raw.opportunities ?? []) as string[];
  const gaps = (raw.gaps ?? []) as string[];
  const completeness_score = (raw.completeness_score ?? 0) as number;

  return {
    address,
    parcel_id,
    property_type,
    assessed_value,
    building_sf,
    acreage,
    current_owner,
    completeness_score,
    risk_score,
    investor_tax_annual,
    tax_delta,
    median_price,
    median_price_per_sf,
    threats,
    opportunities,
    gaps,
    pir_status: pir,
  };
}

/**
 * Transform threat_opportunity data, mapping gaps to actionable questions.
 */
function transformThreatOpportunity(raw: Record<string, unknown>) {
  const threats = (raw.threats ?? []) as string[];
  const opportunities = (raw.opportunities ?? []) as string[];
  const gaps = (raw.gaps ?? []) as string[];
  const gap_questions = gaps
    .map((g) => ({ gap: g, question: GAP_QUESTIONS_MAP[g] ?? "" }))
    .filter((gq) => gq.question);

  return { threats, opportunities, gaps, gap_questions };
}

function renderComponent(
  tool: string,
  data: Record<string, unknown>,
  onFollowUp?: (message: string) => void
) {
  // Skip rendering for error/no_results responses
  if (data.status && data.status !== "ok") return null;

  switch (tool) {
    case "find_comparable_sales":
      return (
        <ComparableSalesMap
          data={data as any}
          onFollowUp={onFollowUp}
        />
      );

    case "get_sales_trend":
      return <PriceTrendChart data={transformSalesTrend(data)} />;
    case "get_market_snapshot":
      return <MarketSnapshotCard data={transformMarketSnapshot(data)} />;
    case "calculate_investor_tax":
      return <TaxImpactCard data={transformTaxData(data)} />;

    case "find_similar_parcels":
      return <SiteSearchMap data={transformSiteSearch(data) as any} onFollowUp={onFollowUp} />;
    case "compare_submarkets":
      return <SubmarketComparison data={transformSubmarkets(data)} />;
    case "lookup_parcel_by_address":
    case "lookup_parcel_by_id":
      return <ParcelDetailCard data={transformParcelDetail(data) as any} onFollowUp={onFollowUp} />;

    case "generate_coa_scenarios":
      return <COACard data={data as any} />;

    case "dsr_progress":
      return (
        <DSRProgressCard
          completeness={(data.completeness_score ?? 0) as number}
          pirStatus={(data.pir_status ?? {}) as Record<string, string>}
        />
      );

    case "deal_sweep_progress":
      return <SweepProgressCard data={data as any} />;

    case "deal_intel":
      return <DealIntelCard data={transformDealIntel(data)} onFollowUp={onFollowUp} />;

    case "threat_opportunity":
      return <ThreatOpportunityCard data={transformThreatOpportunity(data)} onFollowUp={onFollowUp} />;

    default:
      return null;
  }
}

function ComponentLoading({ tool }: { tool: string }) {
  const label = tool.replace(/_/g, " ");
  return (
    <div
      style={{
        padding: "20px",
        background: "var(--surface, #111827)",
        border: "1px solid var(--border, #1e293b)",
        borderRadius: "12px",
        display: "flex",
        alignItems: "center",
        gap: "10px",
        color: "var(--text-muted, #64748b)",
        fontSize: "13px",
      }}
    >
      <span className="spinner" />
      Loading {label} visualization…
    </div>
  );
}
